import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertCircle, ArrowRight, Check, Pencil, Plus, Trash2 } from "lucide-react";
import { useSnapshot } from "valtio";
import { useLocation, useNavigate } from "react-router-dom";
import {
  editorState,
  getEditorStepsFromCurrentEditorState,
} from "../../utils/editor";

type ChecklistItem = {
  id: string;
  title: string;
  completed: boolean;
  automatic?: boolean;
  custom?: boolean;
  systemCompleted?: boolean;
  actionPath?: string;
  actionLabel?: string;
};

type SystemOverrides = Record<string, boolean>;

const manualDefaults: ChecklistItem[] = [
  { id: "budget", title: "Set your wedding budget", completed: false },
  { id: "vendors", title: "Confirm your key vendors", completed: false },
  { id: "outfits", title: "Finalise outfits and fittings", completed: false },
  { id: "timeline", title: "Review your big-day timeline", completed: false },
];

const readSavedItems = (key: string): ChecklistItem[] => {
  try {
    const saved = window.localStorage.getItem(key);
    const parsed = saved ? JSON.parse(saved) : null;
    return Array.isArray(parsed)
      ? parsed.filter(
          (item): item is ChecklistItem =>
            Boolean(item) &&
            typeof item.id === "string" &&
            typeof item.title === "string" &&
            typeof item.completed === "boolean",
        )
      : manualDefaults.map((item) => ({ ...item }));
  } catch {
    return manualDefaults.map((item) => ({ ...item }));
  }
};

const readSystemOverrides = (key: string): SystemOverrides => {
  try {
    const saved = window.localStorage.getItem(key);
    const parsed = saved ? JSON.parse(saved) : {};
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
};

const AddChecklistComposer = ({
  value,
  onChange,
  onAdd,
}: {
  value: string;
  onChange: (value: string) => void;
  onAdd: () => void;
}) => {
  const dockRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const dock = dockRef.current;
    const composer = composerRef.current;
    if (!dock || !composer) return;
    let frame = 0;
    const position = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const dockRect = dock.getBoundingClientRect();
        const height = composer.getBoundingClientRect().height;
        const bottom = Number.parseFloat(getComputedStyle(composer).bottom) || 16;
        const floatingTop = window.innerHeight - bottom - height;
        const dockedTop = dockRect.top + (dockRect.height - height) / 2;
        const translateY = Math.min(0, dockedTop - floatingTop);
        composer.style.transform = `translate(-50%, ${translateY}px)`;
        composer.style.boxShadow = translateY < -1 ? "none" : "0 18px 45px rgba(15, 23, 42, 0.16)";
      });
    };
    position();
    document.addEventListener("scroll", position, true);
    window.addEventListener("resize", position);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("scroll", position, true);
      window.removeEventListener("resize", position);
    };
  }, []);

  return (
    <div ref={dockRef} className="my-weddn-composer-dock h-[72px] w-full">
      {createPortal(
        <div
          ref={composerRef}
          style={{ transform: "translate(-50%, 0)", width: "calc(100% - 32px)", maxWidth: "704px" }}
          className="my-weddn-composer fixed bottom-[max(16px,env(safe-area-inset-bottom))] left-1/2 z-40 flex gap-2 rounded-2xl border border-white/80 bg-white/90 p-2 backdrop-blur-xl transition-shadow"
        >
          <input
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && onAdd()}
            placeholder="Add another checklist item"
            className="my-weddn-composer__input min-w-0 flex-1 bg-transparent px-3 text-sm outline-none md:text-base"
          />
          <button type="button" onClick={onAdd} className="my-weddn-composer__button flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white" aria-label="Add checklist item">
            <Plus size={19} />
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
};

export const MyWeddnChecklist = ({
  compact = false,
  onProgress,
}: {
  compact?: boolean;
  onProgress?: (completed: number, total: number) => void;
}) => {
  const wedding = useSnapshot(editorState);
  const navigate = useNavigate();
  const location = useLocation();
  const storageKey = `my-weddn-checklist:${wedding.weddingID || "current"}`;
  const systemStorageKey = `${storageKey}:system-overrides`;
  const [manualItems, setManualItems] = useState<ChecklistItem[]>(() =>
    readSavedItems(storageKey),
  );
  const [newItem, setNewItem] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [systemOverrides, setSystemOverrides] = useState<SystemOverrides>(() =>
    readSystemOverrides(systemStorageKey),
  );

  useEffect(() => {
    setManualItems(readSavedItems(storageKey));
    setSystemOverrides(readSystemOverrides(systemStorageKey));
  }, [storageKey, systemStorageKey]);

  useEffect(() => {
    window.localStorage.setItem(storageKey, JSON.stringify(manualItems));
  }, [manualItems, storageKey]);

  useEffect(() => {
    window.localStorage.setItem(systemStorageKey, JSON.stringify(systemOverrides));
  }, [systemOverrides, systemStorageKey]);

  const plannerBase = location.pathname.startsWith("/planner/weddings/")
    ? location.pathname.match(/^\/planner\/weddings\/[^/]+/)?.[0] || ""
    : "";

  const automaticItems = useMemo<ChecklistItem[]>(() => {
    const steps = getEditorStepsFromCurrentEditorState(editorState);
    return [
      {
        id: "wedding-details",
        title: "Add your wedding details",
        completed: systemOverrides["wedding-details"] ?? steps.hashtagComplete,
        systemCompleted: steps.hashtagComplete,
        automatic: true,
        actionPath: `${plannerBase}/website/edit/basics`,
        actionLabel: "Update details",
      },
      {
        id: "schedule",
        title: "Create your event schedule",
        completed: systemOverrides.schedule ?? steps.eventSchedulesComplete,
        systemCompleted: steps.eventSchedulesComplete,
        automatic: true,
        actionPath: `${plannerBase}/website/edit/event-schedule`,
        actionLabel: "Add schedule",
      },
      {
        id: "website",
        title: "Set up your wedding website",
        completed: systemOverrides.website ?? steps.shouldDisplayPreview,
        systemCompleted: steps.shouldDisplayPreview,
        automatic: true,
        actionPath: `${plannerBase}/website/edit`,
        actionLabel: "Set up website",
      },
      {
        id: "share-website",
        title: "Share your wedding website",
        completed: systemOverrides["share-website"] ?? steps.shareWebsiteComplete,
        systemCompleted: steps.shareWebsiteComplete,
        automatic: true,
        actionPath: `${plannerBase}/website`,
        actionLabel: "Share website",
      },
    ];
  }, [plannerBase, systemOverrides, wedding]);

  const items = [...automaticItems, ...manualItems];
  const completedCount = items.filter((item) => item.completed).length;

  useEffect(() => {
    onProgress?.(completedCount, items.length);
  }, [completedCount, items.length, onProgress]);

  const updateItem = (id: string, changes: Partial<ChecklistItem>) => {
    setManualItems((current) =>
      current.map((item) => (item.id === id ? { ...item, ...changes } : item)),
    );
  };

  const toggleItem = (item: ChecklistItem) => {
    const completed = !item.completed;
    if (item.automatic) {
      setSystemOverrides((current) => ({ ...current, [item.id]: completed }));
      return;
    }
    updateItem(item.id, { completed });
  };

  const addItem = () => {
    const title = newItem.trim();
    if (!title) return;
    setManualItems((current) => [
      ...current,
      { id: `custom-${Date.now()}`, title, completed: false, custom: true },
    ]);
    setNewItem("");
  };

  return (
    <div className={`my-weddn-checklist ${compact ? "space-y-3" : "space-y-5"}`}>
      <div className="my-weddn-progress">
        <div className="my-weddn-progress__header mb-2 flex items-end justify-between gap-4">
          <div>
            <p className="text-sm text-slate-500">Wedding readiness</p>
            <p className="text-lg font-medium text-slate-900">
              {completedCount} of {items.length} complete
            </p>
          </div>
          <span className="text-sm font-medium text-slate-600">
            {Math.round((completedCount / Math.max(items.length, 1)) * 100)}%
          </span>
        </div>
        <div className="my-weddn-progress__track h-2 overflow-hidden rounded-full bg-slate-100">
          <div
            className="my-weddn-progress__value h-full rounded-full bg-[#4f9665] transition-all"
            style={{ width: `${(completedCount / Math.max(items.length, 1)) * 100}%` }}
          />
        </div>
      </div>

      <div className={`my-weddn-task-list space-y-2 ${compact ? "max-h-64 overflow-y-auto pr-1" : ""}`}>
        {items.map((item) => (
          <div key={item.id}>
          <div className="my-weddn-task group flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
            <button
              type="button"
              onClick={() => toggleItem(item)}
              aria-label={`${item.completed ? "Mark incomplete" : "Mark complete"}: ${item.title}`}
              className={`my-weddn-task__check flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors ${
                item.completed
                  ? "border-[#4f9665] bg-[#4f9665] text-white"
                  : "border-slate-300 bg-white"
              } cursor-pointer`}
            >
              {item.completed && <Check size={14} strokeWidth={2.5} />}
            </button>

            {editingId === item.id ? (
              <input
                autoFocus
                value={item.title}
                onChange={(event) => updateItem(item.id, { title: event.target.value })}
                onBlur={() => setEditingId(null)}
                onKeyDown={(event) => event.key === "Enter" && setEditingId(null)}
                className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2 py-1 text-base outline-none focus:border-slate-600"
              />
            ) : (
              <span className={`my-weddn-task__title min-w-0 flex-1 text-sm ${item.completed ? "text-slate-400 line-through" : "text-slate-700"}`}>
                {item.title}
              </span>
            )}

            {!item.automatic && (
              <div className="my-weddn-task__actions flex shrink-0 items-center gap-1">
                <button type="button" aria-label={`Edit ${item.title}`} onClick={() => setEditingId(item.id)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                  <Pencil size={14} />
                </button>
                {item.custom && (
                  <button type="button" aria-label={`Delete ${item.title}`} onClick={() => setManualItems((current) => current.filter(({ id }) => id !== item.id))} className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            )}
          </div>
          {item.automatic && item.completed !== item.systemCompleted && !compact && (
            <div className="mx-2 mt-1.5 flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <AlertCircle size={15} className="shrink-0" />
              <span className="min-w-0 flex-1">
                {item.systemCompleted
                  ? "Weddn currently sees this as completed."
                  : "Weddn cannot verify this as completed yet."}
              </span>
              <button type="button" onClick={() => item.actionPath && navigate(item.actionPath)} className="flex shrink-0 items-center gap-1 font-semibold underline underline-offset-2">
                {item.actionLabel || "Review"} <ArrowRight size={13} />
              </button>
            </div>
          )}
          </div>
        ))}
      </div>

      {!compact && (
        <AddChecklistComposer value={newItem} onChange={setNewItem} onAdd={addItem} />
      )}
    </div>
  );
};
